import { useEffect, useState } from "react";
import RangeSlider from "../../../components/atomic/RangeSlider";
import Input from "../../../components/composed/FormElements/Input";
import { LoanTermsEnum } from "../interface";
import { useUtilityStates } from "../useStates";
import {
  calculatePercentage,
  getAmountWarningMessage,
  getTotalDisbursalAmount,
  showTenureMessage,
} from "./OfferCalculatorFunctions";
import { Tab } from "@headlessui/react";
import classNames from "classnames";
import { formatNumberIntl } from "../../../shared/utils/DateUtil";

const LoanTerms = () => {
  const { offerCalculation, setOfferCalculation, sidebar, existingOffers } =
    useUtilityStates((state) => state);

  let offerDetails = existingOffers[sidebar.offerIndex || 0];
  const [selectedLoanTenure, setSelectedLoanTenure]: any = useState(null);
  const categories = {
    Mo: [],
    Yr: [],
  };
  const [loanAmountWarningMessage, setLoanAmountWarningMessage] =
    useState<string>("");

  const handleLoanTermsChange = (value: number, termType: number) => {
    let tempOfferCalculation = { ...offerCalculation };
    if (termType != LoanTermsEnum.LOAN_TENURE) {
      value = Math.abs(value);
    }
    switch (termType) {
      case LoanTermsEnum.LOAN_AMOUNT:
        tempOfferCalculation.loanAmount = value;
        break;
      case LoanTermsEnum.LOAN_TENURE:
        tempOfferCalculation.loanTenure = value;

        break;
      case LoanTermsEnum.RATE_OF_INTERSET:
        tempOfferCalculation.rateOfInterset = value;
        break;
      default:
        break;
    }

    setOfferCalculation(tempOfferCalculation);
  };

  useEffect(() => {
    let tempOfferCalculation = { ...offerCalculation };
    let additionalParameters = { ...offerDetails.additionalParameters };
    tempOfferCalculation.loanAmount = Number(additionalParameters.maxAmount);
    tempOfferCalculation.loanTenure =
      Number(additionalParameters.maxTenure) || 10;
    tempOfferCalculation.rateOfInterset = offerDetails.interest || 10;
    setOfferCalculation(tempOfferCalculation);
  }, [offerDetails]);

  const isTenureMessage = showTenureMessage(
    offerCalculation.loanTenure,
    offerCalculation.tenureType,
    offerDetails.additionalParameters.maxTenure,
    offerDetails.additionalParameters.minTenure
  );

  const handleTabClick = (index: number) => {
    if (index != selectedLoanTenure) {
      // Destructure to make the code cleaner
      let tempOfferCalculation = { ...offerCalculation };
      let loanTenure = Number(tempOfferCalculation.loanTenure);
      switch (index) {
        case 1: // Monthly to Yearly
          tempOfferCalculation.loanTenure = Number(
            (loanTenure / 12).toFixed(2)
          );
          tempOfferCalculation.tenureType = 0;
          break;
        case 0: // Yearly to Monthly
          tempOfferCalculation.loanTenure = Math.round(loanTenure * 12);
          tempOfferCalculation.tenureType = 1;
          break;
        default:
          return; // Early exit if index is not recognized
      }

      // Update state only if there are changes
      setSelectedLoanTenure(index);
      setOfferCalculation(tempOfferCalculation);
    } // No changes if the same tab is clicked
  };

  useEffect(() => {
    const warningMessage = getAmountWarningMessage(
      offerCalculation.loanAmount,
      offerDetails.additionalParameters.minAmount,
      offerDetails.additionalParameters.maxAmount
    );
    setLoanAmountWarningMessage(warningMessage ?? "");
  }, [offerCalculation.loanAmount]);

  return (
    <div className="space-y-4 pl-16">
      <div className=" text-2xl font-medium leading-8 text-left font-inter">
        {offerDetails.sourceEntityName}
      </div>

      <div className=" text-sm text-gray-500">{offerDetails.lenderName}</div>

      <div className="flex items-center   justify-between">
        <div className="text-sm">Loan Amount</div>
        <Input
          type="number"
          onChange={(event: any) => {
            if (event.target.value.length < 20) {
              handleLoanTermsChange(
                event.target.value,
                LoanTermsEnum.LOAN_AMOUNT
              );
            }
          }}
          value={Number(offerCalculation.loanAmount) || ""}
        />
      </div>
      <div>
        <RangeSlider
          onChange={(value) => {
            handleLoanTermsChange(value, LoanTermsEnum.LOAN_AMOUNT);
          }}
          value={Number(offerCalculation.loanAmount)}
          min={0}
          max={Number(offerDetails.additionalParameters.maxAmount)}
          id="loanAmount"
        />
      </div>
      {loanAmountWarningMessage && (
        <div className="text-xs text-red-400 pt-0">
          {loanAmountWarningMessage}
        </div>
      )}
      <div className="flex items-center   justify-between">
        <div className="text-sm">Rate Of Interest (p.a)</div>
        <Input
          type="number"
          onChange={(event: any) => {
            if (event.target.value.length < 5) {
              handleLoanTermsChange(
                event.target.value,
                LoanTermsEnum.RATE_OF_INTERSET
              );
            }
          }}
          value={offerCalculation.rateOfInterset || null}
        ></Input>
      </div>
      <RangeSlider
        onChange={(value) => {
          handleLoanTermsChange(value, LoanTermsEnum.RATE_OF_INTERSET);
        }}
        value={offerCalculation.rateOfInterset || 0}
        min={0}
        max={50}
        id="rateOfInterset"
      />
      {Number(offerCalculation.rateOfInterset) !=
        Number(offerDetails.interest) && (
        <div className="text-xs text-red-400 pt-0">
          Interest rate not applicable for offer: {offerDetails.interest}
        </div>
      )}

      <div className="flex  items-center    justify-between">
        <div className="text-sm">Loan Tenure</div>
        <div className="flex items-center gap-2">
          <div>
            <Tab.Group>
              <Tab.List className="flex w-20 space-1 rounded-xl bg-gray-900/20 p-1">
                {/*  */}
                {Object.keys(categories).map((category, index) => (
                  <Tab
                    key={category}
                    onClick={(event: any) => {
                      handleTabClick(index);
                    }}
                    className={({ selected }) =>
                      classNames(
                        " w-10 rounded-lg py-1.5 text-sm font-medium leading-3",
                        "ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2",
                        selected
                          ? "bg-white text-blue-700 shadow"
                          : "text-white hover:bg-white/[0.12] hover:text-white"
                      )
                    }
                  >
                    {category}
                  </Tab>
                ))}
              </Tab.List>
            </Tab.Group>
          </div>
          <Input
            onBlur={(event: any) => {
              let value = event.target.value;
              if (value == "") {
                handleLoanTermsChange(
                  offerCalculation.tenureType == 0 ? 1 : 12,
                  LoanTermsEnum.LOAN_TENURE
                );
              }
            }}
            type="number"
            onChange={(event: any) => {
              if (event.target.value.length < 5) {
                handleLoanTermsChange(
                  event.target.value,
                  LoanTermsEnum.LOAN_TENURE
                );
              }
            }}
            value={offerCalculation.loanTenure}
          />
        </div>
      </div>
      <RangeSlider
        onChange={(value) => {
          handleLoanTermsChange(value, LoanTermsEnum.LOAN_TENURE);
        }}
        value={offerCalculation.loanTenure}
        min={0}
        max={
          offerCalculation.tenureType == 0
            ? Number(offerDetails.additionalParameters.maxTenure)
            : Number(offerDetails.additionalParameters.maxTenure) * 12
        }
        id="loanTenure"
      />

      {offerCalculation.loanTenure && isTenureMessage && (
        <div className="text-xs text-red-400 pt-0">
          Loan Tenure is out of offer range:{" "}
          {offerDetails.additionalParameters.minTenure} Months to{" "}
          {offerDetails.additionalParameters.maxTenure} Months
        </div>
      )}

      <div className=" text-sm">Loan Offer Details</div>

      <div className="flex    justify-between items-center">
        <div className=" text-sm text-gray-500">Loan Amount</div>
        <div>(+) {formatNumberIntl(offerCalculation.loanAmount)}</div>
      </div>
      <div className="flex   justify-between items-center">
        <div className=" text-sm text-gray-500">Processing Fees</div>
        <div>
          (-){" "}
          {formatNumberIntl(
            calculatePercentage(
              offerCalculation.loanAmount,
              offerDetails.processingFee
            )
          )}
        </div>
      </div>
      <div className="flex   justify-between items-center">
        <div className=" text-sm text-gray-500">GST 18 %</div>
        <div>
          (-){" "}
          {offerCalculation.loanAmount
            ? formatNumberIntl(
                calculatePercentage(
                  calculatePercentage(
                    offerCalculation.loanAmount,
                    offerDetails.processingFee
                  ),
                  18
                )
              )
            : 0}
        </div>
      </div>

      <div className="flex   justify-between">
        Total Disbursal Amount
        <div>{getTotalDisbursalAmount(offerCalculation, offerDetails)}</div>
      </div>
    </div>
  );
};

export default LoanTerms;
